import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router';

import IconCheck from '@/assets/svg/icon-check.svg?react';
import IconClose from '@/assets/svg/icon-close.svg?react';
import IconUserAdmin from '@/assets/svg/icon-user-admin.svg?react';
import IconUser from '@/assets/svg/icon-user.svg?react';

import { cn } from '@/lib/utils';

import { useIsMobile } from '@/hooks/use-mobile';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ConfirmationDialog } from '@/components/ConfirmationDialog/ConfirmationDialog';
import ParticleBackground from '@/components/particles-background';
import { UserSearch } from '@/components/UserSearch/UserSearch';

import { useAuthStore } from '@/stores/authStore';
import { useGlobalStore } from '@/stores/globalStore';

import { apiService } from '@/services/api-service';

import { USER_ROLES } from '@/constants/global-constants';
import { privateRoutes } from '@/constants/route-settings';
import { SEARCH_PARAMS } from '@/constants/search-params';

export function UserListPage() {
  const { getAutoFetchUsers, updateUser } = useGlobalStore();
  const { updateUserActiveStatus } = useAuthStore();
  const isMobile = useIsMobile();

  // Lazy loading state
  const [users, setUsers] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [totalUserUuids, setTotalUserUuids] = useState<string[]>([]);

  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isSearchActive, setIsSearchActive] = useState(false);

  const [userToBlock, setUserToBlock] = useState<{
    uuid: string;
    is_active: boolean;
  } | null>(null);

  const BATCH_SIZE = 50;

  // Fetch all user UUIDs via ElasticSearch
  const searchAllUsers = useCallback(async (): Promise<string[]> => {
    try {
      const query = {
        query: {
          bool: {
            must: [
              {
                term: {
                  table: 'users',
                },
              },
            ],
          },
        },
        size: 10000,
        _source: ['public_users_uuid'],
      };

      const response = await apiService.elasticSearch(query);

      return response.hits.hits.map((hit) => hit._source.public_users_uuid);
    } catch (error) {
      console.error('ElasticSearch failed:', error);
      throw error;
    }
  }, []);

  // Initial load - get all user UUIDs and load first batch
  useEffect(() => {
    const fetchInitialUsers = async () => {
      setIsLoading(true);
      try {
        // Run both UUID fetching operations in parallel
        const [allUserUuidsBackend, allUserUuids] = await Promise.all([
          getAutoFetchUsers(),
          searchAllUsers(),
        ]);

        // Filter out backend user UUIDs from ElasticSearch results to prevent duplicates
        const backendUserUuids = new Set(
          allUserUuidsBackend.map((user) => user.uuid),
        );
        const filteredElasticUuids = allUserUuids.filter(
          (uuid) => !backendUserUuids.has(uuid),
        );
        setTotalUserUuids(filteredElasticUuids);

        // Then fetch the first batch of actual user data from filtered ElasticSearch UUIDs
        const firstBatchUuids = filteredElasticUuids.slice(0, BATCH_SIZE);
        const fetchedUsers = await getAutoFetchUsers(firstBatchUuids);

        const combinedUsers = [...allUserUuidsBackend, ...fetchedUsers];
        setUsers(combinedUsers);

        // Adjust pagination based on how many users we already have
        const totalUsersLoaded = combinedUsers.length;
        const pagesLoaded = Math.ceil(totalUsersLoaded / BATCH_SIZE);
        setCurrentPage(pagesLoaded);
        setHasMore(filteredElasticUuids.length > totalUsersLoaded);
      } catch (error) {
        console.error('Failed to fetch users:', error);
        setUsers([]);
        setHasMore(false);
      } finally {
        setIsLoading(false);
      }
    };

    fetchInitialUsers();
  }, []);

  // Load more users when scrolling
  const loadMoreUsers = useCallback(async () => {
    if (!hasMore || isLoadingMore || isLoading) return;

    setIsLoadingMore(true);
    try {
      const startIndex = currentPage * BATCH_SIZE;
      const endIndex = startIndex + BATCH_SIZE;
      const nextBatchUuids = totalUserUuids.slice(startIndex, endIndex);

      if (nextBatchUuids.length === 0) {
        setHasMore(false);
        return;
      }

      const fetchedUsers = await getAutoFetchUsers(nextBatchUuids);

      setUsers((prevUsers) => [...prevUsers, ...fetchedUsers]);
      setCurrentPage((prevPage) => prevPage + 1);
      setHasMore(endIndex < totalUserUuids.length);
    } catch (error) {
      console.error('Failed to load more users:', error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [
    hasMore,
    isLoadingMore,
    isLoading,
    currentPage,
    totalUserUuids,
    getAutoFetchUsers,
  ]);

  // Handle scroll detection
  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;

      // Check if scrolled to bottom (with small threshold)
      if (scrollHeight - scrollTop <= clientHeight + 50) {
        loadMoreUsers();
      }
    },
    [loadMoreUsers],
  );

  // Search users via ElasticSearch
  const searchUsersElastic = useCallback(
    async (field: string, value: string): Promise<string[]> => {
      try {
        let query;

        switch (field) {
          case SEARCH_PARAMS.EMAIL:
            query = {
              query: {
                bool: {
                  must: [
                    {
                      term: {
                        table: 'users',
                      },
                    },
                    {
                      wildcard: {
                        public_users_email: {
                          value: `*${value.toLowerCase()}*`,
                          case_insensitive: true,
                        },
                      },
                    },
                  ],
                },
              },
              size: 500,
              _source: ['public_users_uuid'],
            };
            break;

          case SEARCH_PARAMS.LAST_NAME:
            query = {
              query: {
                bool: {
                  must: [
                    {
                      term: {
                        table: 'users',
                      },
                    },
                    {
                      wildcard: {
                        public_users_last_name: {
                          value: `*${value.toLowerCase()}*`,
                          case_insensitive: true,
                        },
                      },
                    },
                  ],
                },
              },
              size: 500,
              _source: ['public_users_uuid'],
            };
            break;

          case SEARCH_PARAMS.PHONE:
            query = {
              query: {
                bool: {
                  must: [
                    {
                      term: {
                        table: 'users',
                      },
                    },
                    {
                      wildcard: {
                        public_users_phone_number: {
                          value: `*${value}*`,
                          case_insensitive: true,
                        },
                      },
                    },
                  ],
                },
              },
              size: 500,
              _source: ['public_users_uuid'],
            };
            break;

          case SEARCH_PARAMS.USER_TYPE:
            // Find the role value by searching in USER_ROLES
            const userRole = Object.values(USER_ROLES).find((role) =>
              role.title.toLowerCase().includes(value.toLowerCase()),
            );

            if (!userRole) {
              return [];
            }

            query = {
              query: {
                bool: {
                  must: [
                    {
                      term: {
                        table: 'users',
                      },
                    },
                    {
                      match: {
                        public_users_user_type: userRole.value,
                      },
                    },
                  ],
                },
              },
              size: 500,
              _source: ['public_users_uuid'],
            };
            break;

          default:
            return [];
        }

        const response = await apiService.elasticSearch(query);
        return response.hits.hits.map((hit) => hit._source.public_users_uuid);
      } catch (error) {
        console.error('ElasticSearch user search failed:', error);
        return [];
      }
    },
    [],
  );

  // Handle search with local + ElasticSearch
  const handleSearch = useCallback(
    async (params: { field: string | null; value: string }) => {
      if (!params.field || !params.value.trim()) {
        // Clear search
        setIsSearchActive(false);
        setSearchResults([]);
        return;
      }

      setIsSearching(true);
      setIsSearchActive(true);

      try {
        // Step 1: Search in local state first
        const localResults = users.filter((user) => {
          const searchValue = params.value.toLowerCase();
          switch (params.field) {
            case SEARCH_PARAMS.EMAIL:
              return user.email.toLowerCase().includes(searchValue);
            case SEARCH_PARAMS.LAST_NAME:
              return user.last_name.toLowerCase().includes(searchValue);
            case SEARCH_PARAMS.PHONE:
              return user.phone_number?.toLowerCase().includes(searchValue);
            case SEARCH_PARAMS.USER_TYPE:
              const userRole = Object.values(USER_ROLES).find((role) =>
                role.title.toLowerCase().includes(searchValue),
              );
              return userRole?.value === user.user_type;
            default:
              return false;
          }
        });

        // Step 2: Search via ElasticSearch for additional results
        const elasticUuids = await searchUsersElastic(
          params.field,
          params.value,
        );

        // Step 3: Fetch additional users that aren't already loaded
        const localUserUuids = new Set(users.map((user) => user.uuid));
        const newUserUuids = elasticUuids.filter(
          (uuid) => !localUserUuids.has(uuid),
        );

        let additionalUsers: any[] = [];
        if (newUserUuids.length > 0) {
          additionalUsers = await getAutoFetchUsers(newUserUuids);
        }

        // Step 4: Combine results without duplicates
        const combinedResults = [...localResults, ...additionalUsers];
        setSearchResults(combinedResults);
      } catch (error) {
        console.error('Search failed:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    },
    [users, searchUsersElastic, getAutoFetchUsers],
  );

  const confirmBlockUser = async () => {
    if (!userToBlock) return;

    const statusChangeTo = userToBlock.is_active;
    await updateUserActiveStatus(userToBlock.uuid, statusChangeTo);
    await updateUser(userToBlock.uuid, { is_active: statusChangeTo });
    setUserToBlock(null);
  };

  // Show search results if search is active, otherwise show all users
  const displayUsers = isSearchActive ? searchResults : users;

  const handleBlockUser = (uuid: string, is_active: boolean) => () => {
    setUserToBlock({
      uuid,
      is_active: !is_active,
    });
  };

  return (
    <>
      <ConfirmationDialog
        isOpen={userToBlock !== null}
        onClose={() => setUserToBlock(null)}
        onConfirm={confirmBlockUser}
        variant={userToBlock?.is_active ? 'default' : 'destructive'}
        title={`Ви впевнені, що бажаєте ${
          userToBlock?.is_active ? 'активувати' : 'заблокувати'
        } цей запис?`}
        confirmText={userToBlock?.is_active ? 'Активувати' : 'Заблокувати'}
      />
      <div className="relative flex h-full w-full flex-col overflow-hidden bg-page-background">
        <div className="absolute left-0 top-0 z-10 h-48 w-full overflow-hidden">
          <ParticleBackground />
        </div>
        <div className="relative z-20 mx-auto flex w-full max-w-4xl flex-grow flex-col overflow-hidden px-4 pb-6 pt-12 sm:px-6 sm:pb-12 sm:pt-24">
          <h1 className="mb-6 flex-shrink-0 text-2xl font-semibold text-white">
            Ваш пошуковий запит
          </h1>
          <div className="mb-8 flex-shrink-0">
            <UserSearch onSearch={handleSearch} />
          </div>

          <div className="flex max-h-[700px] flex-grow flex-col overflow-y-auto rounded-md border bg-white shadow-[0px_0px_10px_2px_#00000040]">
            <div className="sticky top-0 z-10 flex flex-shrink-0 items-center justify-between border-b bg-white p-5 pr-3.5 shadow-[0px_0px_10px_2px_#00000040]">
              <h2 className="text-2xl font-semibold xl:text-xl">
                Усі користувачі
              </h2>
              <Link to={privateRoutes[USER_ROLES.ADMIN.value].addUser}>
                <Button
                  size="long"
                  className="border border-primary bg-transparent px-4 py-1 text-primary hover:bg-primary hover:text-white"
                >
                  Додати користувача
                </Button>
              </Link>
            </div>
            <div
              className="flex-grow overflow-y-auto"
              onScroll={isSearchActive ? undefined : handleScroll}
            >
              {isLoading ? (
                <div className="flex items-center justify-center p-8">
                  <div className="flex items-center gap-3">
                    <div className="h-6 w-6 animate-spin-slow rounded-full border-2 border-transparent border-l-primary border-t-primary"></div>
                    <span className="text-gray-500">
                      Завантаження користувачів...
                    </span>
                  </div>
                </div>
              ) : isSearching ? (
                <div className="flex items-center justify-center p-8">
                  <div className="flex items-center gap-3">
                    <div className="h-6 w-6 animate-spin-slow rounded-full border-2 border-transparent border-l-primary border-t-primary"></div>
                    <span className="text-gray-500">Пошук користувачів...</span>
                  </div>
                </div>
              ) : (
                <>
                  {displayUsers.map((user: any) => (
                    <div
                      key={user.uuid}
                      className="flex items-center justify-between border-b p-4"
                    >
                      <div className="flex items-center gap-4">
                        <div className="flex h-12 w-12 items-center justify-center rounded bg-blue-300">
                          {user.user_type ===
                          USER_ROLES.EXTERNAL_SYSTEM.value ? (
                            <IconUserAdmin className="h-10 w-10" />
                          ) : (
                            <IconUser className="h-10 w-10" />
                          )}
                        </div>
                        <div className="flex flex-col gap-1">
                          <div className="text-xs font-medium leading-none text-blue-300">
                            {user.first_name} {user.last_name}
                          </div>
                          <div className="text-primary">{user.email}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        {!isMobile ? (
                          <span
                            className={cn(
                              'text-sm font-semibold text-primary',
                              !user.is_active && 'text-destructive',
                            )}
                          >
                            {user.is_active ? 'Активний' : 'Неактивний'}
                          </span>
                        ) : (
                          <div
                            className={cn(
                              'h-[8px] w-[8px] rounded-full',
                              user.is_active ? 'bg-success' : 'bg-destructive',
                            )}
                          ></div>
                        )}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              ⋯
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent>
                            <DropdownMenuItem className="p-0">
                              <Link
                                to={`${privateRoutes[USER_ROLES.ADMIN.value].editUser.replace(':uuid', user.uuid)}`}
                                className="flex items-center px-5 py-3.5 text-sm text-primary"
                              >
                                <IconUser className="mr-3 h-[18px] w-[14px] stroke-primary" />
                                Профіль
                              </Link>
                            </DropdownMenuItem>
                            {user.is_active ? (
                              <DropdownMenuItem
                                className="flex items-center justify-between text-sm text-destructive hover:text-destructive"
                                onClick={handleBlockUser(
                                  user.uuid,
                                  user.is_active,
                                )}
                              >
                                <IconClose className="mr-3 h-[14px] w-[14px] fill-destructive stroke-destructive" />
                                Заблокувати
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem
                                className="flex items-center justify-between text-sm text-primary hover:text-primary"
                                onClick={handleBlockUser(
                                  user.uuid,
                                  user.is_active,
                                )}
                              >
                                <IconCheck className="mr-3 h-[14px] w-[14px] fill-primary stroke-primary" />
                                Активувати
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  ))}
                  {!isSearchActive && isLoadingMore && (
                    <div className="flex items-center justify-center p-4">
                      <div className="flex items-center gap-2">
                        <div className="h-4 w-4 animate-spin-slow rounded-full border-2 border-transparent border-l-primary border-t-primary"></div>
                        <span className="text-sm text-gray-500">
                          Завантаження ще користувачів...
                        </span>
                      </div>
                    </div>
                  )}
                  {!isSearchActive && !hasMore && users.length > 0 && (
                    <div className="flex items-center justify-center p-4">
                      <span className="text-sm text-gray-400">
                        Усі користувачі завантажені ({users.length} всього)
                      </span>
                    </div>
                  )}
                  {isSearchActive &&
                    searchResults.length === 0 &&
                    !isSearching && (
                      <div className="flex items-center justify-center p-4">
                        <span className="text-sm text-gray-400">
                          Користувачів не знайдено
                        </span>
                      </div>
                    )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
